/**
 * Search Defaults Composable
 *
 * Provides safe access to global ibeSearchDefaults configuration object
 * populated by the backend (Pimcore/Symfony).
 *
 * This composable follows the same pattern as useTranslate.ts for consistency.
 * The ibeSearchDefaults variable is declared globally in index.html without const/let/var.
 *
 * Usage:
 * ```ts
 * import { loadIbeSearchDefaults } from '@/composables/filters/useSearchDefaults';
 *
 * const defaults = loadIbeSearchDefaults();
 * if (defaults) {
 *   console.log('Destination:', defaults.dest);
 *   console.log('Durations:', defaults.durations);
 * }
 * ```
 */

import type { IbeSearchDefaults } from '@/types/SearchDefaults';

declare const ibeSearchDefaults: IbeSearchDefaults | undefined;

/**
 * Load ibeSearchDefaults from global scope
 *
 * @returns Backend configuration or null
 *
 * @example
 * ```ts
 * const defaults = loadIbeSearchDefaults();
 *
 * if (defaults) {
 *   // Configuration available
 *   const destId = defaults.dest; // "123"
 *   const durations = defaults.durations; // [3, 5, 7, 10, 14, 21]
 * } else {
 *   // Use hardcoded defaults
 *   console.warn('No ibeSearchDefaults found, using fallback');
 * }
 * ```
 */
export function loadIbeSearchDefaults(): IbeSearchDefaults | null {
  if (typeof ibeSearchDefaults === 'undefined') {
    return null;
  }

  const globalDefaults = ibeSearchDefaults;

  if (typeof globalDefaults === 'object' && globalDefaults !== null) {
    return normalizeDefaults(globalDefaults);
  }

  return null;
}

function normalizeDefaults(defaults: IbeSearchDefaults): IbeSearchDefaults {
  if (!Array.isArray(defaults.dest?.value)) return defaults;
  return {
    ...defaults,
    dest: {
      ...defaults.dest,
      value: defaults.dest.value.map((c) => (typeof c === 'string' ? c.toUpperCase() : c)),
    },
  };
}
